<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import AccountLogin from "@/components/account/AccountLogin.vue";
import AccountInfo from "@/components/account/AccountInfo.vue";
import StatusDialog from "@/components/general/StatusDialog.vue";
import { logoInnax } from "@/services/images";
import { getStatusPageComponents, severityCode } from "@/services/api/info.api";

const tabIndex = ref(null);

const timeCountDown = computed(() => {
  //When subtracting dates the result is given in milliseconds
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;

  const dateNow = new Date();
  const dateParisProof = new Date("2050-01-01");

  return Math.floor((dateParisProof - dateNow) / _MS_PER_DAY);
});


interface StatusComponent {
  id: string;
  name: string;
  status: string;
  updatedAt: string;
  description?: string;
  groupId?: string;
}

const isLoading = ref(true);
const showDialog = ref(false);
const components = ref<StatusComponent[]>([]);
const componentsLoading = ref(false);

// Bepaal de ergste status van alle components
const overallStatus = computed(() => {
  const statusPriority: Record<string, number> = {
    "major_outage": 4,
    "down": 4,
    "partial_outage": 3,
    "degraded_performance": 2,
    "under_maintenance" : 1,
    "operational": 0
  };

  if (components.value.length === 0) {
    return "operational";
  }

  let bestStatus = "operational";
  let maxPriority = 0;

  for (const component of components.value) {
    const priority = statusPriority[component.status] || 0;
    if (priority > maxPriority) {
      maxPriority = priority;
      bestStatus = component.status;
    }
  }

  return bestStatus;
});

async function fetchStatusComponents() {
  try {
    isLoading.value = true;
    const response = await getStatusPageComponents();
    if (response.severity <= severityCode.warning && response.data) {
      components.value = response.data;
    }
  } catch (err) {
    console.error("Error fetching status components:", err);
  } finally {
    isLoading.value = false;
  }
}

function openStatusDialog() {
  showDialog.value = true;
  if (components.value.length === 0) {
    fetchStatusComponents();
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case "operational":
      return "mdi-check-circle";
    case "under_maintenance":
      return "mdi-wrench";
    case "degraded_performance":
    case "partial_outage":
      return "mdi-alert-circle";
    case "major_outage":
    case "down":
      return "mdi-close-circle";
    default:
      return "mdi-help-circle";
  }
}

onMounted(() => {
  fetchStatusComponents();
});
</script>

<template>
  <v-main>
    <div class="account-page">
      <div class="welcome">
        <div class="logo">
          <v-img alt="INNAX Logo" :src="logoInnax" class="image" />
        </div>
        <v-tabs v-model="tabIndex" class="tabToggles" selected-class="active">
          <v-tab value="logon">Aanmelden</v-tab>
          <v-tab value="info">Meer informatie</v-tab>
        </v-tabs>

        <v-tabs-window v-model="tabIndex">
          <v-tabs-window-item value="logon"> <account-login></account-login> </v-tabs-window-item>

          <v-tabs-window-item value="info"> <account-info></account-info> </v-tabs-window-item>
        </v-tabs-window>
      </div>

      
      <div class="main-image">
        <p>
          Nog <strong>{{ timeCountDown }} dagen</strong> om<br />Paris Proof te halen
        </p>
      </div>  

      </div>
    <footer class="footer-content">
      <button 
        v-if="isLoading" 
        class="status-button status-subtle"
        disabled
      >
        <v-progress-circular indeterminate size="12"></v-progress-circular>
      </button>
      <button 
        v-else-if="components.length > 0" 
        class="status-button status-subtle" 
        :class="`status-${overallStatus}`"
        @click="openStatusDialog"
        title="Klik voor status details"
      >
        <v-icon size="x-small">{{ getStatusIcon(overallStatus) }}</v-icon>
        <span>Bekijk applicatie Status</span>
      </button>
    </footer>
    <status-dialog 
      v-model="showDialog" 
      :components="components"
      :is-loading="componentsLoading"
    ></status-dialog>
  </v-main>

</template>

<style scoped>
  .footer-content {
    padding-left: 16px;
    margin-top: -30px;
  }
</style>